import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Field, Select } from '@shared/ui/Input';

export const Bar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

// Переключатель «источник» наверху страницы. Делит корпус документов
// на смысловые группы: внешние НПА, корпоративные шаблоны, всё подряд.
// Эти данные физически лежат в одном RAG-хранилище — переключатель меняет
// только представление и API-эндпоинт.
export const SourceTabs = styled.div`
  display: flex;
  gap: 2px;
  padding: 3px;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  width: fit-content;
`;

export const SourceTab = styled.button<{ $active: boolean }>`
  background: ${({ $active, theme }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  border: none;
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  transition:
    background-color 120ms ease,
    color 120ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const FilterRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
`;

export const FilterLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ErrorBox = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  background: ${({ theme }) => theme.colors.status.danger}15;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3.5)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

export const Empty = styled.td`
  text-align: center;
  padding: ${({ theme }) => theme.spacing(10)};
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
`;

export const FileList = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  max-height: 220px;
  overflow-y: auto;
`;

export const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  gap: ${({ theme }) => theme.spacing(2)};

  &:last-child {
    border-bottom: none;
  }
`;

export const FileName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

export const FileSize = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 12px;
`;

export const Remove = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0 ${({ theme }) => theme.spacing(2)};
  transition: color ${({ theme }) => theme.motion.fast};
  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
  }
`;

// ─── Вкладка «Шаблоны исходящих»: слоты по типам ────────────────────────
// Инвариант «один тип — один включённый шаблон» доносится визуально:
// активный шаблон выделен, прочие версии свёрнуты под ним.

export const Note = styled.div`
  background: ${({ theme }) => theme.colors.primarySoft};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.55;
`;

export const SlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const Slot = styled(Card)`
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export const SlotHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const SlotTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
`;

// Активный (включённый) шаблон — выделенная плашка внутри слота.
export const ActiveBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const EmptySlot = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  text-align: center;
`;

export const TplName = styled.div`
  font-weight: 500;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TplMeta = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const BtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
`;

// «Свёрнутый» список прочих версий типа. Native details — доступно и без
// лишнего state; стилизуем summary под ссылку.
export const OtherVersions = styled.details`
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: ${({ theme }) => theme.spacing(2)};

  > summary {
    cursor: pointer;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.text.secondary};
    user-select: none;
    list-style: none;
    &::-webkit-details-marker {
      display: none;
    }
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

export const VersionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
`;

// Предупреждение в форме загрузки: новый шаблон вытеснит текущий активный.
export const WarnNote = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.status.warning}14;
  border: 1px solid ${({ theme }) => theme.colors.status.warning}44;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3.5)};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.primary};
`;

// ─── Styled Components replacing inline styles ─────────────────────────

export const KbFilterSelect = styled(Select)`
  width: 200px;
`;

export const KbTableCard = styled(Card)`
  padding: 0;
  overflow: hidden;
`;

export const KbLoadingCell = styled.td`
  text-align: center;
  padding: ${({ theme }) => theme.spacing(8)};
`;

export const KbTitleCell = styled.td`
  font-weight: 500;
`;

export const KbMonoCell = styled.td`
  font-size: 13px;
`;

export const KbStatusWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const KbErrorMessage = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.status.danger};
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: help;
`;

export const KbDateCell = styled.td`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const KbRowActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  justify-content: flex-end;
`;

export const UploadGridTwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

export const UploadFieldCompact = styled(Field)`
  margin: 0;
`;

export const UploadFieldSpaced = styled(Field)`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

export const UploadModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const ActiveTemplateHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const VersionActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;
